import React, { useEffect, useMemo, useState } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import hotelNoImg from "../../../assets/images/hotels/no_img.png";
import { collectHotelListingImageUrls } from "../hotelListingDedupe";

const ROOM_CAROUSEL_MAX = 20;

const extractImageUrl = (item) => {
  if (item == null) return "";
  if (typeof item === "string") return item.trim();
  if (typeof item !== "object") return "";
  return String(
    item.url ??
      item.Url ??
      item.tns ??
      item.Tns ??
      item.path ??
      item.Path ??
      item.src ??
      item.Src ??
      item.imageUrl ??
      item.ImageUrl ??
      item.image ??
      item.Image ??
      item.link ??
      item.Link ??
      "",
  ).trim();
};

const collectImageUrls = (...sources) => {
  const urls = [];
  const seen = new Set();
  for (const source of sources) {
    if (typeof source === "string") {
      const url = source.trim();
      if (!url || url.length < 8 || seen.has(url)) continue;
      if (/no_img|placeholder|HotelNA\.jpg|no_photo/i.test(url)) continue;
      seen.add(url);
      urls.push(url);
      continue;
    }
    if (!Array.isArray(source)) continue;
    for (const item of source) {
      const url = extractImageUrl(item);
      if (!url || url.length < 8 || seen.has(url)) continue;
      if (/no_img|placeholder|HotelNA\.jpg|no_photo/i.test(url)) continue;
      seen.add(url);
      urls.push(url);
    }
  }
  return urls;
};

const rotateImages = (images, offset = 0) => {
  if (!Array.isArray(images) || images.length === 0) return [];
  const start =
    ((Number(offset) || 0) % images.length + images.length) % images.length;
  if (start === 0) return images;
  return images.slice(start).concat(images.slice(0, start));
};

/**
 * Room card image carousel (same UX as Hotels search listing).
 * Prefer room-level images; fall back to hotel gallery images.
 * imageOffset rotates gallery so adjacent rooms don't look identical.
 */
const HotelCardImage = ({
  hotelDetailsRespObj,
  hotelRoom,
  onHandleModal,
  imageOffset = 0,
  autoplay = false,
  autoplaySpeed = 2000,
}) => {
  const images = useMemo(() => {
    // Permissive room + hotel URL extract (string or {url|tns|...}).
    const roomAndHotelUrls = collectImageUrls(
      hotelRoom?.roomImageList,
      hotelRoom?.RoomImageList,
      hotelRoom?.images,
      hotelRoom?.Images,
      hotelRoom?.imageList,
      hotelRoom?.ImageList,
      hotelDetailsRespObj?.images,
      hotelDetailsRespObj?.Images,
      hotelDetailsRespObj?.imageList,
      hotelDetailsRespObj?.ImageList,
    );
    // Listing collector adds propertyImages / hotelImages shapes.
    const listingUrls = collectHotelListingImageUrls(hotelDetailsRespObj || {});
    const all = [];
    const seen = new Set();
    for (const url of [...roomAndHotelUrls, ...listingUrls]) {
      if (!url || seen.has(url)) continue;
      seen.add(url);
      all.push(url);
    }
    return rotateImages(all, imageOffset);
  }, [hotelRoom, hotelDetailsRespObj, imageOffset]);

  const carouselImages = useMemo(
    () => images.slice(0, ROOM_CAROUSEL_MAX),
    [images],
  );

  const [imageIndex, setImageIndex] = useState(0);
  useEffect(() => {
    setImageIndex(0);
  }, [hotelRoom?.roomId, carouselImages.length, imageOffset]);

  useEffect(() => {
    if (!autoplay || carouselImages.length <= 1) return undefined;
    const timer = setInterval(() => {
      setImageIndex((i) => (i + 1) % carouselImages.length);
    }, Math.max(Number(autoplaySpeed) || 2000, 500));
    return () => clearInterval(timer);
  }, [autoplay, autoplaySpeed, carouselImages.length]);

  const openGallery = (startIndex = imageIndex) => {
    onHandleModal?.({
      roomName: hotelRoom?.roomName || hotelRoom?.RoomName || "Room images",
      images,
      _startImageIndex: startIndex,
    });
  };

  const goPrev = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (carouselImages.length <= 1) return;
    setImageIndex(
      (i) => (i - 1 + carouselImages.length) % carouselImages.length,
    );
  };

  const goNext = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (carouselImages.length <= 1) return;
    setImageIndex((i) => (i + 1) % carouselImages.length);
  };

  if (carouselImages.length === 0) {
    return (
      <div className="hotel_image_">
        <img src={hotelNoImg} alt={hotelRoom?.roomName || "no-photo"} />
      </div>
    );
  }

  const safeIndex = Math.min(imageIndex, carouselImages.length - 1);

  return (
    <div className="hotel_image_ hotel_image_carousel room-card-image-carousel">
      <button
        type="button"
        className="hotel-image-open"
        onClick={() => openGallery(safeIndex)}
        aria-label="Open room images"
      >
        <img
          src={carouselImages[safeIndex]}
          alt={hotelRoom?.roomName || "room"}
          onError={(e) => {
            e.target.src = require("../../../assets/images/hotelfallbackimg.jpg");
          }}
        />
      </button>

      {carouselImages.length > 1 ? (
        <>
          <button
            type="button"
            className="hotel-carousel-nav hotel-carousel-nav--prev"
            aria-label="Previous image"
            onClick={goPrev}
          >
            <LeftOutlined />
          </button>
          <button
            type="button"
            className="hotel-carousel-nav hotel-carousel-nav--next"
            aria-label="Next image"
            onClick={goNext}
          >
            <RightOutlined />
          </button>
          <div className="hotel-carousel-counter">
            {safeIndex + 1}/{carouselImages.length}
          </div>
        </>
      ) : null}

      <div
        className="gallery-icon"
        onClick={() => openGallery(safeIndex)}
        title="View gallery"
      >
        <i className="fa fa-picture-o" aria-hidden="true" />
      </div>
    </div>
  );
};

export default HotelCardImage;
